// The article editor's body: one card per block, in order, with the Add row.
// Blocks are structured (vortex-api lib/helpContent.ts); text carries **bold**
// only for the exact words on a Scout button or label.

import { ArrowDownIcon, ArrowUpIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { HelpBlock, HelpMedia } from './data/help';

const input = 'min-h-[44px] w-full rounded-[10px] border border-slate-400/20 bg-slate-900/60 px-3 text-[15px] text-white placeholder:text-slate-500 focus:border-cyan-400/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/40';
const area = `${input} min-h-[64px] py-2.5`;
/** Enough rows to show the whole text at this column's width (about 40 characters a line). */
const rowsFor = (text: string) => Math.max(2, text.split('\n').reduce((n, line) => n + Math.ceil((line.length || 1) / 40), 0));
const small = 'inline-flex min-h-[36px] items-center gap-1.5 rounded-[8px] px-2.5 text-[13px] font-medium text-slate-300 hover:bg-slate-400/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 disabled:opacity-40';

const NAMES: Record<HelpBlock['type'], string> = {
  paragraph: 'Text', subheading: 'Subheading', note: 'Note', steps: 'Steps', image: 'Image', clip: 'Clip', video: 'Video', table: 'Table', link: 'Link',
};

/** What the Add row makes. Video waits for video upload. */
const ADDS: { label: string; make: () => HelpBlock }[] = [
  { label: 'Text', make: () => ({ type: 'paragraph', text: '' }) },
  { label: 'Subheading', make: () => ({ type: 'subheading', text: '' }) },
  { label: 'Steps', make: () => ({ type: 'steps', items: [{ text: '' }] }) },
  { label: 'Note', make: () => ({ type: 'note', text: '' }) },
  { label: 'Image', make: () => ({ type: 'image', mediaId: '' }) },
  { label: 'Clip', make: () => ({ type: 'clip', mediaId: '' }) },
  { label: 'Table', make: () => ({ type: 'table', head: ['', ''], rows: [['', '']] }) },
  { label: 'Link', make: () => ({ type: 'link', topicId: '' }) },
];

export interface ArticleOption {
  topicId: string;
  title: string;
}

interface Props {
  blocks: HelpBlock[];
  onChange: (blocks: HelpBlock[]) => void;
  media: HelpMedia[];
  articles: ArticleOption[];
}

export function BlocksEditor({ blocks, onChange, media, articles }: Props) {
  const set = (i: number, b: HelpBlock) => onChange(blocks.map((x, j) => (j === i ? b : x)));
  const move = (i: number, by: number) => {
    const next = [...blocks];
    [next[i], next[i + by]] = [next[i + by], next[i]];
    onChange(next);
  };
  return (
    <div className="space-y-3">
      {blocks.length === 0 && <p className="text-[15px] text-slate-400">No body yet. Add a block below.</p>}
      {blocks.map((b, i) => (
        <section key={i} aria-label={`${NAMES[b.type]} ${i + 1}`} className="space-y-2.5 rounded-xl border border-slate-400/15 bg-slate-800/45 p-3">
          <header className="flex items-center gap-1">
            <span className="flex-1 text-[13px] font-semibold text-slate-300">{NAMES[b.type]} {i + 1}</span>
            <button type="button" className={small} aria-label={`Move ${NAMES[b.type]} ${i + 1} up`} disabled={i === 0} onClick={() => move(i, -1)}><ArrowUpIcon className="h-4 w-4" /></button>
            <button type="button" className={small} aria-label={`Move ${NAMES[b.type]} ${i + 1} down`} disabled={i === blocks.length - 1} onClick={() => move(i, 1)}><ArrowDownIcon className="h-4 w-4" /></button>
            <button type="button" className={small} aria-label={`Remove ${NAMES[b.type]} ${i + 1}`} onClick={() => onChange(blocks.filter((_, j) => j !== i))}><TrashIcon className="h-4 w-4" /></button>
          </header>
          <BlockFields block={b} onChange={(nb) => set(i, nb)} media={media} articles={articles} index={i} />
        </section>
      ))}
      <div className="flex flex-wrap items-center gap-1.5 rounded-xl border border-dashed border-slate-400/25 p-2.5">
        <span className="px-1 text-[13px] text-slate-400">Add</span>
        {ADDS.map((a) => (
          <button key={a.label} type="button" className={`${small} border border-slate-400/20`} onClick={() => onChange([...blocks, a.make()])}>
            <PlusIcon className="h-4 w-4" aria-hidden="true" />{a.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function MediaPicker({ kinds, value, onChange, media, label }: { kinds: HelpMedia['kind'][]; value: string; onChange: (id: string) => void; media: HelpMedia[]; label: string }) {
  const options = media.filter((m) => kinds.includes(m.kind));
  return (
    <label className="flex flex-col gap-1 text-[13px] font-medium text-slate-300">
      {label}
      <select className={input} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{options.length ? 'Choose…' : 'Nothing uploaded yet'}</option>
        {options.map((m) => <option key={m.id} value={m.id}>{m.alt} ({m.kind}, {(m.bytes / 1e6).toFixed(1)} MB)</option>)}
      </select>
    </label>
  );
}

function BlockFields({ block, onChange, media, articles, index }: { block: HelpBlock; onChange: (b: HelpBlock) => void; media: HelpMedia[]; articles: ArticleOption[]; index: number }) {
  const id = `b${index}`;
  switch (block.type) {
    case 'paragraph':
    case 'note':
      return <textarea aria-label={`${NAMES[block.type]} ${index + 1} text`} className={area} rows={rowsFor(block.text)} value={block.text} onChange={(e) => onChange({ ...block, text: e.target.value })} />;
    case 'subheading':
      return <input aria-label={`Subheading ${index + 1} text`} className={input} value={block.text} onChange={(e) => onChange({ ...block, text: e.target.value })} />;
    case 'steps':
      return (
        <ol className="space-y-2.5">
          {block.items.map((s, n) => (
            <li key={n} className="flex gap-2">
              <span className="mt-2.5 w-6 shrink-0 text-right font-mono text-[15px] text-slate-400">{n + 1}</span>
              <div className="flex-1 space-y-1.5">
                <textarea
                  aria-label={`Step ${n + 1}`}
                  className={area}
                  rows={rowsFor(s.text)}
                  value={s.text}
                  onChange={(e) => onChange({ ...block, items: block.items.map((x, j) => (j === n ? { ...x, text: e.target.value } : x)) })}
                />
                <div className="flex flex-wrap items-end gap-2">
                  <div className="min-w-[12rem] flex-1">
                    <MediaPicker
                      label={`Step ${n + 1} image or clip (optional)`}
                      kinds={['image', 'clip']}
                      value={s.mediaId ?? ''}
                      media={media}
                      onChange={(mid) => onChange({ ...block, items: block.items.map((x, j) => (j === n ? { text: x.text, ...(mid ? { mediaId: mid } : {}) } : x)) })}
                    />
                  </div>
                  <button type="button" className={small} disabled={block.items.length === 1} onClick={() => onChange({ ...block, items: block.items.filter((_, j) => j !== n) })}>
                    Remove step {n + 1}
                  </button>
                </div>
              </div>
            </li>
          ))}
          <li>
            <button type="button" className={`${small} ml-8 border border-slate-400/20`} disabled={block.items.length >= 20} onClick={() => onChange({ ...block, items: [...block.items, { text: '' }] })}>
              <PlusIcon className="h-4 w-4" aria-hidden="true" />Step
            </button>
          </li>
        </ol>
      );
    case 'image':
    case 'clip':
      return (
        <div className="space-y-2">
          <MediaPicker label={block.type === 'image' ? 'Image' : 'Clip'} kinds={[block.type]} value={block.mediaId} media={media} onChange={(mid) => onChange({ ...block, mediaId: mid })} />
          <label className="flex flex-col gap-1 text-[13px] font-medium text-slate-300">
            Caption (optional)
            <input className={input} value={block.caption ?? ''} onChange={(e) => onChange({ type: block.type, mediaId: block.mediaId, ...(e.target.value ? { caption: e.target.value } : {}) })} />
          </label>
        </div>
      );
    case 'video':
      return <p className="text-[15px] text-slate-400">Video {block.mediaId}{block.stepsIn ? `, steps in ${block.stepsIn}` : ''}. Video upload comes later.</p>;
    case 'table': {
      const cols = block.head.length;
      const setHead = (c: number, v: string) => onChange({ ...block, head: block.head.map((h, j) => (j === c ? v : h)) });
      const setCell = (r: number, c: number, v: string) => onChange({ ...block, rows: block.rows.map((row, i) => (i === r ? row.map((x, j) => (j === c ? v : x)) : row)) });
      return (
        <div className="space-y-2">
          <div className="overflow-x-auto">
            <table className="w-full border-separate border-spacing-1">
              <thead>
                <tr>{block.head.map((h, c) => <th key={c} scope="col"><input aria-label={`Column ${c + 1} heading`} className={`${input} font-semibold`} value={h} onChange={(e) => setHead(c, e.target.value)} /></th>)}</tr>
              </thead>
              <tbody>
                {block.rows.map((row, r) => (
                  <tr key={r}>{row.map((x, c) => <td key={c}><input aria-label={`Row ${r + 1}, column ${c + 1}`} className={input} value={x} onChange={(e) => setCell(r, c, e.target.value)} /></td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button type="button" className={`${small} border border-slate-400/20`} disabled={cols >= 5} onClick={() => onChange({ ...block, head: [...block.head, ''], rows: block.rows.map((row) => [...row, '']) })}>Add column</button>
            <button type="button" className={small} disabled={cols <= 1} onClick={() => onChange({ ...block, head: block.head.slice(0, -1), rows: block.rows.map((row) => row.slice(0, -1)) })}>Remove last column</button>
            <button type="button" className={`${small} border border-slate-400/20`} disabled={block.rows.length >= 12} onClick={() => onChange({ ...block, rows: [...block.rows, new Array(cols).fill('')] })}>Add row</button>
            <button type="button" className={small} disabled={block.rows.length <= 1} onClick={() => onChange({ ...block, rows: block.rows.slice(0, -1) })}>Remove last row</button>
          </div>
        </div>
      );
    }
    case 'link':
      return (
        <div className="grid gap-2 sm:grid-cols-2">
          <label htmlFor={`${id}-topic`} className="flex flex-col gap-1 text-[13px] font-medium text-slate-300">
            Article
            <select id={`${id}-topic`} className={input} value={block.topicId} onChange={(e) => onChange({ ...block, topicId: e.target.value })}>
              <option value="">Choose…</option>
              {articles.map((a) => <option key={a.topicId} value={a.topicId}>{a.topicId} · {a.title}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[13px] font-medium text-slate-300">
            Link text (optional; the title otherwise)
            <input className={input} value={block.text ?? ''} onChange={(e) => onChange({ type: 'link', topicId: block.topicId, ...(e.target.value ? { text: e.target.value } : {}) })} />
          </label>
        </div>
      );
  }
}
